import React from 'react';
import { Link } from 'react-router-dom';
import { useI18n, ROUTE_MAPPINGS } from '../context/I18nContext';
import { useCookies } from '../context/CookieContext';
import { Heart, Calculator, RotateCcw, CheckCircle, CreditCard, MapPin, Settings, FileText, HelpCircle, Globe, Shield } from 'lucide-react';

const Footer = () => {
  const { t, locale } = useI18n();
  const { openSettings } = useCookies();

  // Build path based on locale
  const buildPath = (routeKey) => {
    const route = ROUTE_MAPPINGS[locale]?.[routeKey] || ROUTE_MAPPINGS.it[routeKey];
    if (locale === 'it') {
      return route ? `/${route}` : '/';
    }
    return route ? `/${locale}/${route}` : `/${locale}`;
  };

  const toolLinks = [
    { routeKey: 'home', label: t('nav.calculate'), icon: Calculator },
    { routeKey: 'reverse', label: t('nav.reverse'), icon: RotateCcw },
    { routeKey: 'verify', label: t('nav.verify'), icon: CheckCircle },
    { routeKey: 'healthCard', label: t('nav.healthCard'), icon: CreditCard },
    { routeKey: 'municipalities', label: t('nav.municipalities'), icon: MapPin },
  ];

  const guideLinks = [
    { routeKey: 'howItWorks', label: t('nav.howItWorks') },
    { routeKey: 'faq', label: 'FAQ' },
    { routeKey: 'guide', label: locale === 'it' ? 'Guida Completa' : 'Complete Guide' },
    { routeKey: 'omocodia', label: 'Omocodia' },
    { routeKey: 'blog', label: 'Blog', isBlog: true },
  ];

  const aboutLabel = { it: 'Chi Siamo', en: 'About Us', de: 'Über uns', fr: 'À propos', es: 'Quiénes Somos', pt: 'Quem Somos', ro: 'Despre Noi', pl: 'O Nas' };
  const contactLabel = { it: 'Contatti', en: 'Contact', de: 'Kontakt', fr: 'Contact', es: 'Contacto', pt: 'Contacto', ro: 'Contact', pl: 'Kontakt' };

  const legalLinks = [
    { routeKey: 'about', label: aboutLabel[locale] || aboutLabel.en },
    { routeKey: 'contact', label: contactLabel[locale] || contactLabel.en },
    { routeKey: 'privacy', label: t('nav.privacy') },
    { routeKey: 'cookie', label: t('nav.cookie') },
    { routeKey: 'terms', label: t('nav.terms') },
    { routeKey: 'accessibility', label: t('nav.accessibility') },
  ];

  // Popular provinces for internal linking (SEO boost)
  const popularProvinces = [
    { sigla: 'RM', name: 'Roma' },
    { sigla: 'MI', name: 'Milano' },
    { sigla: 'NA', name: 'Napoli' },
    { sigla: 'TO', name: 'Torino' },
    { sigla: 'FI', name: 'Firenze' },
    { sigla: 'BA', name: 'Bari' },
  ];

  // Popular regions for internal linking (SEO boost)
  const popularRegions = [
    { slug: 'lombardia', name: 'Lombardia' },
    { slug: 'lazio', name: 'Lazio' },
    { slug: 'campania', name: 'Campania' },
    { slug: 'sicilia', name: 'Sicilia' },
    { slug: 'veneto', name: 'Veneto' },
    { slug: 'piemonte', name: 'Piemonte' },
    { slug: 'emilia-romagna', name: 'Emilia-Romagna' },
    { slug: 'toscana', name: 'Toscana' },
  ];

  // Region path builder
  const getRegionPath = (slug) => {
    const paths = {
      it: 'regione',
      en: 'region',
      de: 'region',
      fr: 'region',
      es: 'region',
      pt: 'regiao',
      ro: 'regiune',
      pl: 'region'
    };
    const path = paths[locale] || 'region';
    return locale === 'it' ? `/${path}/${slug}` : `/${locale}/${path}/${slug}`;
  };

  // Province path builder
  const getProvincePath = (sigla) => {
    const paths = {
      it: 'provincia',
      en: 'province',
      de: 'provinz',
      fr: 'province',
      es: 'provincia',
      pt: 'provincia',
      ro: 'provincia',
      pl: 'prowincja'
    };
    const path = paths[locale] || 'province';
    return locale === 'it' ? `/${path}/${sigla.toLowerCase()}` : `/${locale}/${path}/${sigla.toLowerCase()}`;
  };

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-8">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to={buildPath('home')} className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-lg">🇮🇹</span>
              </div>
              <div>
                <span className="font-bold text-white text-lg">CodiceFiscale</span>
                <span className="text-emerald-400 font-bold">.expert</span>
              </div>
            </Link>
            <p className="text-sm text-gray-400 max-w-md mb-4">
              {t('footer.description')}
            </p>
            {/* Trust indicators */}
            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <Shield className="h-3 w-3" /> GDPR
              </span>
              <span className="flex items-center gap-1">
                <CheckCircle className="h-3 w-3" /> WCAG 2.1 AA
              </span>
              <span className="flex items-center gap-1">
                <Globe className="h-3 w-3" /> 8 {locale === 'it' ? 'Lingue' : 'Languages'}
              </span>
            </div>
          </div>

          {/* Tools */}
          <div>
            <h4 className="font-semibold text-white mb-4">{t('footer.tools')}</h4>
            <ul className="space-y-2">
              {toolLinks.map(({ routeKey, label, icon: Icon }) => (
                <li key={routeKey}>
                  <Link
                    to={buildPath(routeKey)}
                    className="text-sm text-gray-400 hover:text-emerald-400 transition-colors flex items-center gap-2"
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Guide */}
          <div>
            <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              {locale === 'it' ? 'Guide' : 'Guides'}
            </h4>
            <ul className="space-y-2">
              {guideLinks.map(({ routeKey, label, isBlog }) => (
                <li key={routeKey}>
                  <Link
                    to={isBlog ? (locale === 'it' ? '/blog' : `/${locale}/blog`) : buildPath(routeKey)}
                    className="text-sm text-gray-400 hover:text-emerald-400 transition-colors"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Popular Provinces (SEO Internal Links) */}
          <div>
            <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              {locale === 'it' ? 'Province' : 'Provinces'}
            </h4>
            <ul className="space-y-2">
              {popularProvinces.map(({ sigla, name }) => (
                <li key={sigla}>
                  <Link
                    to={getProvincePath(sigla)}
                    className="text-sm text-gray-400 hover:text-emerald-400 transition-colors"
                  >
                    {name} ({sigla})
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Popular Regions (SEO Internal Links) */}
          <div>
            <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
              <Globe className="h-4 w-4" />
              {locale === 'it' ? 'Regioni' : 'Regions'}
            </h4>
            <ul className="space-y-2">
              {popularRegions.map(({ slug, name }) => (
                <li key={slug}>
                  <Link
                    to={getRegionPath(slug)}
                    className="text-sm text-gray-400 hover:text-emerald-400 transition-colors"
                  >
                    {name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-semibold text-white mb-4">{t('footer.legal')}</h4>
            <ul className="space-y-2">
              {legalLinks.map(({ routeKey, label }) => (
                <li key={routeKey}>
                  <Link
                    to={buildPath(routeKey)}
                    className="text-sm text-gray-400 hover:text-emerald-400 transition-colors"
                  >
                    {label}
                  </Link>
                </li>
              ))}
              <li>
                <button
                  onClick={openSettings}
                  className="text-sm text-gray-400 hover:text-emerald-400 transition-colors flex items-center gap-2"
                  data-testid="footer-cookie-settings"
                >
                  <Settings className="h-4 w-4" />
                  {locale === 'it' ? 'Cookie' : 'Cookies'}
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="text-sm text-gray-500 flex flex-col sm:flex-row items-center gap-2">
            <span>{t('footer.copyright')}</span>
            <span className="hidden sm:inline">|</span>
            <a href="mailto:info@codicefiscale.expert" className="hover:text-emerald-400 transition-colors">
              info@codicefiscale.expert
            </a>
          </div>
          <p className="text-sm text-gray-500 flex items-center gap-1">
            {t('footer.madeWith')} <Heart className="h-4 w-4 text-red-500 fill-red-500" /> {t('footer.inItaly')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
